import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cacheAllDropdowns } from '../utils/offlineDb';

const API_BASE = process.env.REACT_APP_API_URL;

const AuthContext = createContext();

// JWT decoding utility
const decodeJWT = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
};

// Check if token is valid and not expired
const isTokenValid = (token) => {
  if (!token) return false;

  try {
    const payload = decodeJWT(token);
    if (!payload?.exp) return false;

    // Check if token is expired or will expire within 5 minutes
    const currentTime = Date.now() / 1000;
    const bufferTime = 300; // 5 minutes in seconds
    return payload.exp > (currentTime + bufferTime);
  } catch (error) {
    console.error('Error checking token validity:', error);
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const cachedUser = localStorage.getItem('user');
      return cachedUser ? JSON.parse(cachedUser) : null;
    } catch (e) {
      console.error('Failed to parse cached user:', e);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('access_token');
    return storedToken && isTokenValid(storedToken) ? storedToken : null;
  });
  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Public routes that don't require auth
  const publicRoutes = useMemo(() => [
    '/login',
    '/forgot-password',
    '/verify-code',
    '/reset-password',
    '/register' // Add if you have registration
  ], []);

  // Check if current route is public
  const isPublicRoute = useMemo(() => {
    return publicRoutes.some(route => location.pathname.startsWith(route));
  }, [location.pathname, publicRoutes]);

  const refreshToken = useCallback(async () => {
    if (refreshInProgress) return null;

    setRefreshInProgress(true);
    try {
      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE}/api/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh })
      });

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const { access } = await response.json();

      // Validate the new token before storing
      if (!isTokenValid(access)) {
        throw new Error('Invalid token received');
      }

      localStorage.setItem('access_token', access);
      setToken(access);
      return access;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    } finally {
      setRefreshInProgress(false);
    }
  }, [refreshInProgress]);

  const authFetch = useCallback(async (url, options = {}) => {
    let currentToken = token || localStorage.getItem('access_token');

    // If no token and we're not on a public route, redirect to login
    if (!currentToken && !isPublicRoute) {
      navigate('/login');
      throw new Error('Authentication required');
    }

    // Add default headers
    const headers = {
      ...options.headers,
      'Content-Type': 'application/json',
    };

    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
    }

    // First try with current token
    let response = await fetch(url, {
      ...options,
      headers
    });

    // If token expired (401) and we have a refresh token, try to refresh
    if (response.status === 401 && currentToken && localStorage.getItem('refresh_token')) {
      try {
        currentToken = await refreshToken();
        if (currentToken) {
          headers['Authorization'] = `Bearer ${currentToken}`;

          // Retry the request with new token
          response = await fetch(url, {
            ...options,
            headers
          });
        }
      } catch (refreshError) {
        // If refresh fails, log out
        if (!isPublicRoute) {
          logout();
        }
        throw new Error('Session expired. Please login again.');
      }
    }

    // If still unauthorized after refresh, handle it
    if (response.status === 401 && !isPublicRoute) {
      logout();
      throw new Error('Authentication failed');
    }

    return response;
  }, [token, refreshToken, navigate, isPublicRoute]);

  const checkAuth = useCallback(async (isInitialCheck = false) => {
    // Skip auth check for public routes
    if (isPublicRoute) {
      setLoading(false);
      if (isInitialCheck) {
        setInitialAuthCheckComplete(true);
      }
      return;
    }

    try {
      const storedToken = localStorage.getItem('access_token');

      // If no token, redirect to login
      if (!storedToken) {
        throw new Error('No token found');
      }

      // Check token validity
      if (!isTokenValid(storedToken)) {
        // Token is invalid or expired, try to refresh
        const refresh = localStorage.getItem('refresh_token');
        if (refresh) {
          try {
            await refreshToken();
          } catch (refreshError) {
            throw new Error('Session expired');
          }
        } else {
          throw new Error('Token expired and no refresh token');
        }
      }

      // Fetch user data if not already cached or if forced refresh
      if (!user || isInitialCheck) {
        const response = await authFetch(`${API_BASE}/api/users/me/`);
        if (!response.ok) {
          throw new Error('Not authenticated');
        }

        const userData = await response.json();

        // Validate user data structure
        if (!userData || !userData.id || !userData.email) {
          throw new Error('Invalid user data received');
        }

        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }

      setToken(storedToken);

    } catch (error) {
      console.error('Auth check failed:', error);

      // Only redirect to login if this wasn't a silent check
      if (!isInitialCheck) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);

        if (!isPublicRoute) {
          navigate('/login', {
            state: {
              from: location.pathname,
              message: error.message
            }
          });
        }
      }
    } finally {
      setLoading(false);
      if (isInitialCheck) {
        setInitialAuthCheckComplete(true);
      }
    }
  }, [location.pathname, navigate, user, authFetch, refreshToken, isPublicRoute]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE}/api/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed. Please check your credentials.');
      }

      const { access, refresh } = await response.json();

      // Validate tokens before storing
      if (!isTokenValid(access) || !refresh) {
        throw new Error('Invalid token received from server');
      }

      // Store tokens
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);

      // Fetch and cache user data
      const userResponse = await fetch(`${API_BASE}/api/users/me/`, {
        headers: {
          'Authorization': `Bearer ${access}`,
          'Content-Type': 'application/json'
        }
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }

      const userData = await userResponse.json();

      // Validate user data
      if (!userData || !userData.id || !userData.email) {
        throw new Error('Invalid user data received');
      }

      // Update state
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(access);

      // Cache all dropdown data in the background so the app works offline immediately
      cacheAllDropdowns(access).catch(err =>
        console.warn('[Offline Cache] Background caching failed:', err)
      );

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);

      // Clean up on error
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);

      return {
        success: false,
        error: error.message || 'An unexpected error occurred during login.'
      };
    }
  };

  const logout = async (redirectToLogin = true) => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        // Try to call logout endpoint, but don't block if it fails
        await fetch(`${API_BASE}/api/logout/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        }).catch(err => console.warn('Logout endpoint failed:', err));
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clean up local state
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);

      if (redirectToLogin) {
        navigate('/login', {
          state: {
            message: 'You have been logged out successfully.'
          }
        });
      }
    }
  };

  // Function to manually check auth (useful for checking permissions)
  const checkAccess = async () => {
    try {
      await checkAuth(false);
      return { hasAccess: true, user };
    } catch (error) {
      return { hasAccess: false, error: error.message };
    }
  };

  // Initial auth check on mount
  useEffect(() => {
    checkAuth(true);
  }, []);

  // Check auth on route changes (but not for public routes)
  useEffect(() => {
    if (initialAuthCheckComplete && !isPublicRoute) {
      checkAuth(false);
    }
  }, [location.pathname, initialAuthCheckComplete, isPublicRoute]);

  // Auto-refresh token before it expires
  useEffect(() => {
    if (!token || isPublicRoute) return;

    const checkTokenExpiry = () => {
      const payload = decodeJWT(token);
      if (payload?.exp) {
        const timeUntilExpiry = (payload.exp * 1000) - Date.now();
        // Refresh if token expires in less than 10 minutes
        if (timeUntilExpiry < 600000 && timeUntilExpiry > 0) {
          refreshToken().catch(err => console.warn('Auto-refresh failed:', err));
        }
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Set up interval to check every minute
    const interval = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(interval);
  }, [token, refreshToken, isPublicRoute]);

  // Memoized context value
  const contextValue = useMemo(() => ({
    user,
    loading,
    token,
    login,
    logout,
    refreshToken,
    authFetch,
    checkAccess,
    isAuthenticated: !!user && isTokenValid(token),
    userRole: user?.role,
    hasPermission: (requiredRole) => {
      if (!user?.role) return false;
      // Role hierarchy: admin > manager > coordinator > officer > temp-staff
      const roleHierarchy = {
        'admin': 5,
        'manager': 4,
        'coordinator': 3,
        'officer': 2,
        'temp-staff': 1
      };
      const userRoleLevel = roleHierarchy[user.role] || 0;
      const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
      return userRoleLevel >= requiredRoleLevel;
    }
  }), [user, loading, token, authFetch, refreshToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};